import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Pagination } from "./AdminShared";
import { fmtDate } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Trash2, Bell } from "lucide-react";

export default function AdminCustomers() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [notify, setNotify] = useState(null);
  const [msg, setMsg] = useState({ title: "", message: "" });

  const { data } = useQuery({
    queryKey: ["admin-customers", search, page],
    queryFn: async () => (await api.get("/admin/customers", { params: { search, page, limit: 10 } })).data,
  });

  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/customers/${id}`),
    onSuccess: () => { toast.success("Customer deleted"); qc.invalidateQueries({ queryKey: ["admin-customers"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const sendNotif = useMutation({
    mutationFn: async () => api.post("/admin/notifications", { user_id: notify.id, title: msg.title, message: msg.message }),
    onSuccess: () => { toast.success("Notification sent"); setNotify(null); setMsg({ title: "", message: "" }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const items = data?.items || [];

  return (
    <div>
      <PageHeader title="Customers" subtitle="Manage registered customers and send notifications" />
      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input data-testid="customer-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search by name or email…" className="pl-10 bg-white/5 border-white/10 text-white" />
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-slate-400">Name</TableHead>
                <TableHead className="text-slate-400">Email</TableHead>
                <TableHead className="text-slate-400">Phone</TableHead>
                <TableHead className="text-slate-400">Orders</TableHead>
                <TableHead className="text-slate-400">Active Subs</TableHead>
                <TableHead className="text-slate-400">Joined</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">No customers found.</TableCell></TableRow>
              ) : items.map((c) => (
                <TableRow key={c.id} className="border-white/10" data-testid={`customer-row-${c.id}`}>
                  <TableCell className="text-white font-medium">{c.name}</TableCell>
                  <TableCell className="text-slate-400">{c.email}</TableCell>
                  <TableCell className="text-slate-400">{c.phone || "—"}</TableCell>
                  <TableCell className="text-slate-300">{c.orders_count}</TableCell>
                  <TableCell className="text-slate-300">{c.active_subs}</TableCell>
                  <TableCell className="text-slate-400 text-sm">{fmtDate(c.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button data-testid={`customer-notify-${c.id}`} size="icon" variant="ghost" onClick={() => setNotify(c)} className="h-8 w-8 text-slate-300 hover:text-white"><Bell className="w-4 h-4" /></Button>
                      <Button data-testid={`customer-delete-${c.id}`} size="icon" variant="ghost" onClick={() => { if (confirm(`Delete ${c.name}?`)) remove.mutate(c.id); }} className="h-8 w-8 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pagination page={page} total={data?.total || 0} limit={10} onPage={setPage} />
      </div>

      <Dialog open={!!notify} onOpenChange={(v) => !v && setNotify(null)}>
        <DialogContent className="bg-[#0b0c12] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">Notify {notify?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-300">Title</Label><Input data-testid="notif-title" value={msg.title} onChange={(e) => setMsg({ ...msg, title: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-slate-300">Message</Label><Textarea data-testid="notif-message" value={msg.message} onChange={(e) => setMsg({ ...msg, message: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="notif-send" onClick={() => sendNotif.mutate()} disabled={!msg.title || sendNotif.isPending} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">Send Notification</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
